// noinspection JSUnusedGlobalSymbols

/*
 * Defines the highest level of the application.  Sets up top level regions and initializes top level modules.  Starts
 * Backbone history once a user logs in. Includes modal region used to show all modals.
 */

define([
  "marionette",
  "backbone",
  "messaging/messenger",
  "modules/header/headerModule",
  "modules/login/loginModule",
  "modules/footer/footerModule",
  "modules/dashboard/dashboardModule",
  "modules/topicsSplit/topicsSplitModule",
  "modules/contentsSplit/contentsSplitModule",
  "modules/learner/learnerModule",
  "modules/reportSplit/reportSplitModule",
  "modules/substitutions/substitutionsModule",
  "modules/admin/adminModule",
  "modules/modal/modalModule",
  "modules/savingDisplay/savingDisplayModule",
  "modules/downloadDisplay/downloadDisplayModule",
  "modules/dialogWindow/dialogWindowModule",
  "modules/quickCreate/quickCreateModule",
  "modules/reviewer/reviewerModule",
  "modules/actions/actionsModule",
  "modules/authoringAdmin/authoringAdminModule",
  "modules/accountAdmin/accountAdminModule",
  "jquery",
  // Only sets up vents, but need to include to catch events we fire
  "modules/mixpanel/mixpanelSupport",
  // CSS needed
  "jquery-ui/dist/themes/base/jquery-ui.css",
  "css/jquery-ui.theme.css",
  "css/jquery.fileupload.css",
  "css/bootstrap.css",
  "css/datepicker.css",
  "css/font-awesome/css/font-awesome.css",
  "css/rd_layout.css",
  "css/rd_subtle.css",
  "css/slideout.css",
  "select2/dist/css/select2.css",
  "tom-select/dist/css/tom-select.bootstrap4.css",
], function (
  Marionette,
  Backbone,
  Messenger,
  HeaderModule,
  LoginModule,
  FooterModule,
  DashboardModule,
  TopicsSplitModule,
  ContentsSplitModule,
  LearnerModule,
  ReportSplitModule,
  SubstitutionsModule,
  AdminModule,
  ModalModule,
  SavingDisplayModule,
  DownloadDisplayModule,
  DialogWindowModule,
  QuickCreateModule,
  ReviewerModule,
  ActionsModule,
  AuthoringAdminModule,
  AccountAdminModule,
  $,
) {
  "use strict";

  const App = new Marionette.Application();
  Backbone.$ = $;
  Marionette.$ = $;
  App.addRegions({
    main: "#main",
    learner: "#learner",
    header: "#header",
    footer: "#footer",
    modal: {
      selector: "#modal",
      regionType: Marionette.Modal,
    },
    savingModal: {
      selector: "#savingModal",
      regionType: Marionette.Modal,
    },
    downloadModal: {
      selector: "#downloadModal",
      regionType: Marionette.Modal,
    },
    dialogModal: {
      selector: "#dialogModal",
      regionType: Marionette.Modal,
    },
    taskCommentModal: {
      selector: "#taskCommentModal",
      regionType: Marionette.Modal,
    },
  });

  App.main.on(
    "show",
    function () {
      if (App.learner.$el) {
        App.learner.$el.hide();
      }
      App.main.$el.show();
    },
    this,
  );

  App.learner.on(
    "show",
    function () {
      if (App.main.$el) {
        //also close the view--thereby closing the module.  I think we need to do this to preserve data consistency.
        App.main.currentView.close();
        App.main.$el.hide();
      }
      App.learner.$el.show();
    },
    this,
  );

  App.addInitializer(function () {
    //initialize ALL top level components here
    this.headerModule = new HeaderModule({ region: App.header });
    this.footerModule = new FooterModule({ region: App.footer });
    this.savingDisplayModule = new SavingDisplayModule({ region: App.savingModal });
    this.downloadDisplayModule = new DownloadDisplayModule({ region: App.downloadModal });
    this.dialogWindowModule = new DialogWindowModule({ region: App.dialogModal });

    this.modalModule = new ModalModule({ region: App.modal });
    this.loginModule = new LoginModule({ region: App.main });
    this.dashboardModule = new DashboardModule({ region: App.main });
    this.topicsSplitModule = new TopicsSplitModule({ region: App.main });
    this.contentsSplitModule = new ContentsSplitModule({ region: App.main });
    this.learnerModule = new LearnerModule({ region: App.learner });
    this.reportsModule = new ReportSplitModule({ region: App.main });
    this.substitutionsModule = new SubstitutionsModule({ region: App.main });
    this.adminModule = new AdminModule({ region: App.main });
    this.quickCreateModule = new QuickCreateModule({ region: App.main });
    this.reviewerModule = new ReviewerModule({ region: App.main });
    this.actionsModule = new ActionsModule({ region: App.main });
    this.authoringAdminModule = new AuthoringAdminModule({ region: App.main });
    this.accountAdminModule = new AccountAdminModule({ region: App.main });
    //show starting modules
    this.loginModule.loginIfNeeded();
    this.footerModule.show();
  });

  //Start history once the login is complete
  Messenger.vent.on("login:complete", function () {
    console.log("LOGGED IN");
    App.headerModule.show();
    //re-render the footer model so that the date gets appropriately i18n'ed
    App.footerModule.show();
    Backbone.history.start();
  });

  //Hide header when displaying the account administration page
  Messenger.vent.on("toggle:header", function (showHeader) {
    if (showHeader) {
      App.header.$el.show();
    } else {
      App.header.$el.hide();
    }
  });

  /*
   * Method for saving stuff when the browser back/forward buttons are pressed, or the URL is changed manual.
   * When the hashchange event is triggered, this function stops it from propegating.  It then tries to save,
   * and retriggers the event if it succeeds so that Backbone can route it.  We then need to ignore the
   * next hashchange event because it's an "echo" due to the retriggering.  If it fails, then the hash is changed
   * to what it was before the change.  When switching away from new topics/content, a new history item is added (with
   * the new ID).  Because of this, we need to fire another "back" and ignore the following two hashchange events.
   * The function also closes any modals that are open.
   */
  let lastHash = window.location.hash;
  let extraHashChangesToIgnore = 0;
  $(window).on("hashchange", function (e) {
    if (extraHashChangesToIgnore === 0) {
      //make sure this isn't an echo of refiring the hashchange event
      //make sure this is a back/forward button, custom URL change and not a backbone.history.navigate situation
      //or a change event before the history is started (pre-login)
      if (Backbone.history.root && Backbone.history.getFragment() !== Backbone.history.fragment) {
        //close any open modal using the cancel event or close:modal event
        if (App.modal.currentView) {
          //if close:modal gets fired from the cancel event, then don't call it again
          let modalClosed = false;
          Messenger.vent.once(
            "close:modal",
            function () {
              modalClosed = true;
            },
            this,
          );
          App.modalModule.layout.modal.currentView.trigger("cancel");
          if (!modalClosed) {
            Messenger.vent.trigger("close:modal");
          }
        }
        //stop the propagation of this event, and only trigger another event once the save is successful
        e.stopImmediatePropagation();
        const newHash = window.location.hash;
        //make sure we save first, then either continue or revert to the last hash if it failed
        Messenger.save(function (success) {
          if (success) {
            if (window.location.hash !== newHash) {
              //special case: go back because a new hashstate was navigated to during the save
              extraHashChangesToIgnore = 2; //we need to ignore the event for the new hashstate and the change due to
              // the back function below
              window.history.back();
            } else {
              //retrigger the change
              extraHashChangesToIgnore = 1;
              $(window).trigger("hashchange");
            }
          } else {
            window.location.hash = lastHash; //revert back to previous hash
          }
        });
        if (Backbone.history.getFragment() === "accountAdmin") {
          App.header.$el.hide();
        } else {
          App.header.$el.show();
        }
      }
    } else {
      extraHashChangesToIgnore--;
    }
    lastHash = window.location.hash; //keep track of the last Hash
  });

  //Warn the user about pending changes before they close/reload the tab (except at the login page)
  window.onbeforeunload = function () {
    const status = {
      login: false,
      logout: false,
    };
    // Check if the window unload was triggered by the user logging in or logging out,
    // if so then ignore displaying the warning
    Messenger.vent.trigger("app:close", status);

    if (!status.logout && !status.login) {
      return "Changes you made may not be saved.";
    }
  };

  // This will allow clicking a Topic Link in the Response preview to navigate to another topic
  window.suggestedTopic = function (intentName, intentId) {
    const kbId = Messenger.reqres.request("active:kb");
    if (kbId && intentId) {
      Messenger.save(function (success) {
        if (success) {
          Backbone.history.navigate(kbId + "/topic/" + intentId, { trigger: true });
        }
      });
    }
  };

  //Rerender entire site by closing and showing the views in top level regions
  Messenger.vent.on("render:all", function () {
    //App.[region].show(App.[region].currentView);
  });

  window.addEventListener("load", () => App.start());

  return App;
});
